import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";

import { ApiError, getAudit } from "./api";
import type { AuditEvent, AuditQuery } from "./types";
import { SectionLabel, colors, spacing } from "./theme";
import { formatDateTime, centralDateBucket, centralBucketDaysAgo } from "./datetime";

const todayBucket = () => centralDateBucket();

// Playback audit log. Defaults to today's events; supervisors can filter by
// their own email or by contactId. Strategy chip mirrors the search page so
// the operator can see which index the server hit.
export default function AuditPage() {
  const [filters, setFilters] = useState<AuditQuery>({
    dateBucket: todayBucket(),
    actorEmail: "",
    contactId: "",
  });
  // endDate is UI-only — combined with filters.dateBucket it drives range mode
  // (single from/to query when there's an actor/contact anchor, fan-out across
  // days otherwise). Capped at a 7-day span client-side.
  const [endDate, setEndDate] = useState("");
  // Client-side action filter — the server returns all action types; this
  // narrows the table. Cheap to filter locally since result sets are small.
  const [actionFilter, setActionFilter] = useState<string>("");
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [strategy, setStrategy] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function update<K extends keyof AuditQuery>(k: K, v: AuditQuery[K]) {
    setFilters((f) => ({ ...f, [k]: v }));
  }

  const rangeMode = !!(filters.dateBucket && endDate && endDate !== filters.dateBucket);
  const hasAnchor = !!(filters.actorEmail || filters.contactId);

  function setDatePreset(preset: "today" | "yesterday" | "last7" | "clear") {
    if (preset === "clear") {
      setFilters((f) => ({ ...f, dateBucket: "" }));
      setEndDate("");
      return;
    }
    if (preset === "last7") {
      setFilters((f) => ({ ...f, dateBucket: centralBucketDaysAgo(6) }));
      setEndDate(centralDateBucket());
      return;
    }
    setFilters((f) => ({ ...f, dateBucket: preset === "yesterday" ? centralBucketDaysAgo(1) : centralDateBucket() }));
    setEndDate("");
  }

  async function runQuery(append: boolean) {
    setLoading(true);
    setError(null);
    try {
      if (rangeMode) {
        const span = daysBetween(filters.dateBucket!, endDate);
        if (span < 0) throw new Error("End date must be on or after the start date.");
        if (span > 6) throw new Error("Date range is limited to 7 days.");

        const base = stripEmpty(filters);
        delete base.dateBucket;

        if (hasAnchor) {
          // Single byActor or byContact GSI query; SK is plain ISO createdAt,
          // so string from/to bounds the window correctly.
          const res = await getAudit({
            ...base,
            from: isoStartOfDay(filters.dateBucket!),
            to: isoEndOfDay(endDate),
          });
          setEvents(res.events);
          setStrategy(res.strategy);
          setNextCursor(null);
        } else {
          // dateBucket-only — fan out one byDate query per day, merge, sort.
          const days = enumerateDays(filters.dateBucket!, endDate);
          const results = await Promise.all(days.map((d) => getAudit({ ...base, dateBucket: d })));
          const merged = results.flatMap((r) => r.events);
          merged.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
          setEvents(merged);
          setStrategy(`byDate × ${days.length}`);
          setNextCursor(null);
        }
        return;
      }

      const params: AuditQuery = stripEmpty(filters);
      if (append && nextCursor) params.cursor = nextCursor;
      const res = await getAudit(params);
      setEvents(append ? [...events, ...res.events] : res.events);
      setStrategy(res.strategy);
      setNextCursor(res.nextCursor);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.body}` : String(e));
    } finally {
      setLoading(false);
    }
  }

  // Initial load: today's events.
  useEffect(() => {
    runQuery(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleEvents = actionFilter
    ? events.filter((e) => e.action === actionFilter)
    : events;

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: "center", flexWrap: "wrap" }}>
            <Typography variant="caption" sx={{ color: colors.gray500, mr: 0.5 }}>
              Quick dates:
            </Typography>
            <Chip
              label="Today"
              size="small"
              onClick={() => setDatePreset("today")}
              color={filters.dateBucket === todayBucket() && !endDate ? "primary" : "default"}
              variant={filters.dateBucket === todayBucket() && !endDate ? "filled" : "outlined"}
              clickable
            />
            <Chip
              label="Yesterday"
              size="small"
              onClick={() => setDatePreset("yesterday")}
              clickable
              variant="outlined"
            />
            <Chip
              label="Last 7 days"
              size="small"
              onClick={() => setDatePreset("last7")}
              clickable
              variant="outlined"
            />
            <Chip
              label="Clear date"
              size="small"
              onClick={() => setDatePreset("clear")}
              clickable
              variant="outlined"
            />
          </Stack>
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              runQuery(false);
            }}
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: spacing.md,
              alignItems: "end",
            }}
          >
            <TextField
              size="small"
              type="date"
              fullWidth
              label={hasAnchor ? "Date (blank = all)" : "Date"}
              InputLabelProps={{ shrink: true }}
              value={filters.dateBucket ?? ""}
              onChange={(e) => update("dateBucket", e.target.value)}
            />
            <TextField
              size="small"
              type="date"
              fullWidth
              label="End date (range, max 7d)"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <TextField
              size="small"
              label="Actor email"
              placeholder="supervisor@nwn.ai"
              value={filters.actorEmail ?? ""}
              onChange={(e) => update("actorEmail", e.target.value)}
            />
            <TextField
              size="small"
              label="Contact ID / target user"
              placeholder="44943299 or user@email"
              value={filters.contactId ?? ""}
              onChange={(e) => update("contactId", e.target.value)}
            />
            <TextField
              size="small"
              select
              label="Action"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="playback">playback</MenuItem>
              <MenuItem value="promote">promote</MenuItem>
              <MenuItem value="demote">demote</MenuItem>
              <MenuItem value="disable">disable</MenuItem>
            </TextField>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? "Searching…" : "Search"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: "center" }}>
        {strategy && (
          <>
            <SectionLabel>Strategy</SectionLabel>
            <Chip label={strategy} size="small" sx={{ fontFamily: "monospace" }} />
          </>
        )}
        <Typography variant="caption" sx={{ color: colors.gray500 }}>
          {visibleEvents.length} event{visibleEvents.length === 1 ? "" : "s"}
          {actionFilter && events.length !== visibleEvents.length && ` (of ${events.length})`}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton size="small" onClick={() => runQuery(false)} disabled={loading} aria-label="Refresh">
          <RefreshIcon />
        </IconButton>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FileDownloadIcon fontSize="small" />}
          onClick={() => exportCsv(events)}
          disabled={events.length === 0}
        >
          Export CSV
        </Button>
      </Stack>

      <Table
        size="small"
        sx={{
          background: "white",
          border: `1px solid ${colors.gray200}`,
          borderRadius: 1,
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Time</TableCell>
            <TableCell>Actor</TableCell>
            <TableCell>Action</TableCell>
            <TableCell>Target</TableCell>
            <TableCell>Detail</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Source IP</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleEvents.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} sx={{ textAlign: "center", fontStyle: "italic", color: colors.gray500 }}>
                No events match the current filters.
              </TableCell>
            </TableRow>
          ) : (
            visibleEvents.map((e) => (
              <TableRow key={e.auditId}>
                <TableCell sx={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                  {formatDateTime(e.createdAt)}
                </TableCell>
                <TableCell sx={{ fontSize: "0.85rem" }}>{e.actorEmail}</TableCell>
                <TableCell>
                  <Chip
                    label={e.action}
                    size="small"
                    variant="outlined"
                    color={actionColor(e.action)}
                  />
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.85rem", color: colors.primary }}>
                  {e.contactId}
                </TableCell>
                <TableCell sx={{ fontSize: "0.85rem" }}>{e.agentId || <Box sx={{ color: colors.gray500 }}>—</Box>}</TableCell>
                <TableCell sx={{ fontSize: "0.85rem", maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis" }}>
                  {e.reason ?? <Box sx={{ color: colors.gray500 }}>—</Box>}
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem", color: colors.gray500 }}>
                  {e.sourceIp ?? "—"}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {nextCursor && !rangeMode && (
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Button variant="outlined" onClick={() => runQuery(true)} disabled={loading}>
            Load more
          </Button>
        </Box>
      )}
    </Box>
  );
}

function actionColor(action: string): "default" | "primary" | "warning" | "error" {
  if (action === "promote") return "primary";
  if (action === "demote") return "warning";
  if (action === "disable") return "error";
  return "default"; // playback + unknown
}

function stripEmpty(p: AuditQuery): AuditQuery {
  const out: AuditQuery = {};
  for (const [k, v] of Object.entries(p)) {
    if (v != null && v !== "") (out as Record<string, unknown>)[k] = v;
  }
  return out;
}

function isoStartOfDay(d: string): string {
  return `${d}T00:00:00.000Z`;
}

function isoEndOfDay(d: string): string {
  return `${d}T23:59:59.999Z`;
}

function daysBetween(start: string, end: string): number {
  return Math.round((Date.parse(end) - Date.parse(start)) / 86_400_000);
}

function enumerateDays(start: string, end: string): string[] {
  const out: string[] = [];
  const d = new Date(`${start}T00:00:00Z`);
  const stop = new Date(`${end}T00:00:00Z`);
  while (d <= stop) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

// CSV export of audit rows. Browser-side only; same RFC 4180 quoting as
// recordings export.
function exportCsv(events: AuditEvent[]) {
  const headers = [
    "Time (UTC)",
    "Actor",
    "Action",
    "Contact ID",
    "Agent",
    "Reason",
    "Source IP",
    "User agent",
    "URL expires",
  ];
  const rows = events.map((e) => [
    e.createdAt,
    e.actorEmail,
    e.action,
    e.contactId,
    e.agentId,
    e.reason ?? "",
    e.sourceIp ?? "",
    e.userAgent ?? "",
    e.urlExpiresAt ?? "",
  ]);
  const csv = [headers, ...rows]
    .map((r) => r.map(escapeCsvCell).join(","))
    .join("\r\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `audit-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function escapeCsvCell(v: string | number): string {
  const s = String(v ?? "");
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
